import { httpClient } from '../common/httpclient';

/**
 * Serviço específico para manipulação de produtos não genéricos (padrão)
 * Centraliza toda a lógica relacionada a produtos cadastrados
 */
class StandardProductService {
    /**
     * Adiciona um produto padrão ao carrinho
     * @param {Object} item - Dados do produto
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async addItem(item, saleId) {
        if(item.update_quantity) {
            return await httpClient.post(`/sales/${saleId}/updateitem`, {
                product_id: item.id || item.product_id,
                quantity: item.quantity
            });
        } else {
            return await httpClient.post('/sales/additem', {
                product_id: item.id || item.product_id,
                quantity: item.quantity,
                price: item.price || item.sale_price,
                cost: item.cost,
                sale_id: saleId,
                product_name: item.product_name || item.name,
                is_generic: false,
                update_quantity: item.update_quantity,
                ...(item.item_discount_value ? { item_discount_value: item.item_discount_value } : {})
            });
        }
    }
        
    

    /**
     * Incrementa a quantidade de um produto padrão
     * @param {string} saleId - ID da venda
     * @param {string} productId - ID do produto
     * @returns {Promise<Object>} Resposta da API
     */
    async incrementItem(saleId, productId) {
        return await httpClient.post(`/sales/${saleId}/incrementitem/${productId}`);
    }

    /**
     * Decrementa a quantidade de um produto padrão
     * @param {string} saleId - ID da venda
     * @param {string} productId - ID do produto
     * @returns {Promise<Object>} Resposta da API
     */
    async decrementItem(saleId, productId) {
        return await httpClient.post(`/sales/${saleId}/decrementitem/${productId}`);
    }

    /**
     * Remove um produto padrão do carrinho
     * @param {string} saleId - ID da venda
     * @param {string} productId - ID do produto
     * @returns {Promise<Object>} Resposta da API
     */
    async deleteItem(saleId, productId) {
        return await httpClient.delete(`/sales/${saleId}/removeitem/${productId}`);
    }

    /**
     * Verifica se um produto é padrão (não genérico)
     * @param {Object} product - Produto a ser verificado
     * @returns {boolean} True se for produto padrão
     */
    isStandardProduct(product) {
        return !product.is_generic && !product.isGeneric;
    }

    /**
     * Verifica se já existe um produto padrão no carrinho
     * @param {Array} cart - Carrinho de compras
     * @param {string} productId - ID do produto
     * @returns {Object|null} Produto existente ou null
     */
    findExistingStandardItem(cart, productId) {
        
        const result = cart.find(item => item.product_id === productId);
        
        return result;
    }

    /**
     * Atualiza um item padrão existente no carrinho
     * @param {Object} existingItem - Item existente no carrinho
     * @param {Object} newProduct - Novo produto
     * @param {number} quantity - Quantidade a adicionar
     * @returns {Object} Item atualizado
     */
    updateExistingItem(existingItem, newProduct, quantity) {
        return {
            ...existingItem,
            quantity: existingItem.quantity + quantity,
            sale_price: newProduct.sale_price
        };
    }

    /**
     * Verifica se há estoque disponível para o produto
     * @param {string} productId - ID do produto a ser verificado
     * @returns {Promise<Object>} Resultado da verificação de estoque
     */
    async checkStock(productId) {
        try {
            const response = await httpClient.get(`/stock/${productId}/isAvailableQuantity`);
            return {
                available: response.data.available,
                currentStock: response.data.quantity || 0,
                requestedQuantity: 1,
                data: response.data
            };
        } catch (error) {
            console.error('Erro ao verificar estoque:', error);
            return {
                available: false,
                currentStock: 0,
                requestedQuantity: 1,
                error: error.message,
                data: { available: false }
            };
        }
    }

    /**
     * Valida os dados de um produto padrão
     * @param {Object} product - Produto a ser validado
     * @returns {Object} Resultado da validação
     */
    validateStandardProduct(product) {
        const errors = [];
        
        if (!product.id) {
            errors.push('ID do produto é obrigatório');
        }
        
        if (!product.product_name || !product.product_name.trim()) {
            errors.push('Nome do produto é obrigatório');
        }
        
        if (!product.sale_price || parseFloat(product.sale_price) <= 0) {
            errors.push('Preço de venda deve ser maior que zero');
        }
        
        if (!product.quantity || parseInt(product.quantity) <= 0) {
            errors.push('Quantidade deve ser maior que zero');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Formata um produto padrão para adição ao carrinho
     * @param {Object} product - Produto original
     * @param {number} quantity - Quantidade
     * @returns {Object} Produto formatado
     */
    formatProductForCart(product, quantity) {
        
        return {
            product_id: product.id || product.product_id,
            product_name: product.name || product.product_name,
            price: product.sale_price,
            cost: product.cost || '',
            quantity: quantity,
            update_quantity: product.update_quantity
        };
    }
}

export default new StandardProductService();
